/**
 * v0.5 施策台帳「担当者の複数名対応」(D-924〜D-935): 複数選択 + 担当者ごとの自由記述役割ラベル。
 * 設計: docs/design/v05-board-initiative-multi-owner-detail.md §7-1。
 * EmployeeAffiliationEditor.tsx (NodeMultiSelect + 選択済みIDごとの補足入力欄) のパターンを踏襲するが、
 * 候補は店舗ノードほど階層化されていないためツリーではなくフラットなチェックボックスリストにする。
 *
 * ⚠️ 選択済み一覧を囲む要素に独自の max-h/overflow-y-auto を付けない (呼び出し元のモーダルが
 * 既に overflow-y-auto を持つため、二重スクロールを避ける)。
 */
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';

export type InitiativeOwner = { employee_id: string; role_label: string | null };
type AssignableEmployee = { employee_id: string; external_person_id: string | null; display_name: string };

export function InitiativeOwnersEditor({
  value, onChange, enabled = true,
}: {
  value: InitiativeOwner[];
  onChange: (next: InitiativeOwner[]) => void;
  /** creatable-nodes 選択前など、候補取得を遅延させたい場合に false にする */
  enabled?: boolean;
}) {
  const { data } = useQuery<{ employees: AssignableEmployee[] }>({
    queryKey: ['board-initiatives', 'assignable-employees'],
    queryFn: () => apiFetch('/board-initiatives/assignable-employees'),
    enabled,
  });
  const candidates = data?.employees ?? [];
  const selectedIds = new Set(value.map((o) => o.employee_id));

  const toggle = (employeeId: string) => {
    if (selectedIds.has(employeeId)) {
      onChange(value.filter((o) => o.employee_id !== employeeId));
    } else {
      onChange([...value, { employee_id: employeeId, role_label: null }]);
    }
  };
  const setRoleLabel = (employeeId: string, roleLabel: string) => {
    onChange(value.map((o) => (o.employee_id === employeeId ? { ...o, role_label: roleLabel || null } : o)));
  };

  return (
    <div className="space-y-2">
      <div className="border border-surface-border rounded divide-y divide-surface-border">
        {candidates.length === 0 && (
          <p className="p-2 text-sm text-text-sub">選択可能な社員がいません。</p>
        )}
        {candidates.map((e) => (
          <label key={e.employee_id} className="flex items-center gap-2 p-2 min-h-[44px] text-sm cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-brand-red shrink-0"
              checked={selectedIds.has(e.employee_id)}
              onChange={() => toggle(e.employee_id)}
              aria-label={`${e.display_name} を担当者にする`} />
            <span className="truncate">{e.display_name}</span>
          </label>
        ))}
      </div>

      {value.length > 0 && (
        <fieldset className="space-y-1.5">
          <legend className="block text-xs text-text-sub mb-1">担当者ごとの役割 (任意)</legend>
          {value.map((o) => {
            const name = candidates.find((c) => c.employee_id === o.employee_id)?.display_name ?? o.employee_id;
            return (
              <div key={o.employee_id} className="flex items-center gap-2">
                <span className="w-28 shrink-0 truncate text-sm">{name}</span>
                <input type="text" value={o.role_label ?? ''} maxLength={100}
                  placeholder="例: 進行役" aria-label={`${name}の役割`}
                  onChange={(e) => setRoleLabel(o.employee_id, e.target.value)}
                  className="flex-1 min-h-[44px] px-3 rounded border border-surface-border text-sm" />
              </div>
            );
          })}
        </fieldset>
      )}
    </div>
  );
}
